import { Box } from "@chakra-ui/react";
import { ColorIcon, colors, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ReactNode } from "react";

import { BuildingIcon } from "../components/CostFunnelIcons";
import { Accent, FunnelCard, FunnelDivider, Highlight } from "../components/CostFunnelUI";

/** 라이브러리 아이콘(음식·제단꽃) 크기. 빈소는 커스텀 SVG 라 자체 크기를 쓴다. */
const ITEM_ICON_SIZE = 24;

/**
 * Step 1 - 비용 구성.
 *
 * 장례식장 비용이 빈소·음식·제단꽃 3항목으로 이루어진다는 것을 인지시킨다.
 * 이 스텝만 주황 배너(`banner`)가 H1 **아래**에 들어간다. 뒤 스텝은 배너가 최상단이다.
 */
export default function Step1CostStructure({
  parlorName,
  banner,
  contentPaddingX,
}: {
  parlorName: string;
  /** CompanyInfoBanner. 타이틀 아래에 끼워 넣는다 */
  banner: ReactNode;
  /**
   * 콘텐츠 좌우 패딩(px). 배너만 이 값만큼 음수 마진으로 빼내 화면 좌우에 딱 붙인다.
   * 주황 면이 좌우 여백을 두고 뜨면 띠지가 아니라 카드로 읽혀 존재감이 떨어진다.
   */
  contentPaddingX: number;
}) {
  return (
    <BaseStack width="100%">
      <Box as="h1" className="head_24_b" color={colors.gray900} css={{ textWrap: "pretty" }}>
        {parlorName} 비용은
        <br />
        이렇게 <Accent>구성돼요</Accent>
      </Box>

      <Spacing gap={16} />
      <Box mx={`-${contentPaddingX}px`}>{banner}</Box>

      <FunnelCard>
        <BaseStack width="100%" spacing={16}>
          <CostItem
            icon={<BuildingIcon />}
            title="빈소 및 시설 이용료"
            description="빈소 평수와 사용 일수에 따라 책정돼요"
          />
          <FunnelDivider />
          <CostItem
            icon={
              <ColorIcon name="funeral_restaurant" size={ITEM_ICON_SIZE} color={colors.orange650} />
            }
            title="음식 비용"
            description="조문객 접객에 필요한 식사와 다과"
          />
          <FunnelDivider />
          <CostItem
            icon={
              <ColorIcon
                name="mingcute_sunflower_line"
                size={ITEM_ICON_SIZE}
                color={colors.orange650}
              />
            }
            title="제단꽃"
            description="빈소 제단을 장식하는 꽃 구성"
          />
        </BaseStack>
      </FunnelCard>

      <Spacing gap={18} />
      <Box className="body_14_r" color={colors.gray800}>
        각 항목을 <Highlight>상황에 맞게 선택</Highlight>하는 것이 비용을 줄이는 핵심이에요
      </Box>
    </BaseStack>
  );
}

function CostItem({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <BaseStack direction="horizontal" width="100%" spacing={14} alignItems="flex-start">
      <Box
        flex="none"
        width="40px"
        height="40px"
        borderRadius="12px"
        background={colors.white}
        border={`1px solid ${colors.gray200}`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Box className="subtitle_16_b" color={colors.gray900}>
          {title}
        </Box>
        <Box className="body_14_r" color={colors.gray700} pt="3px">
          {description}
        </Box>
      </Box>
    </BaseStack>
  );
}
